/**
 * @fileoverview Role icon swatch picker
 *
 * Grid of selectable workflow-role icons, mirroring `ColorSwatchPicker`'s
 * visual language so the role form reads as one control set. The first cell
 * clears the selection.
 */

import { X } from "lucide-react";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import { cn } from "@/utils/misc";
import { ROLE_ICONS, ROLE_ICON_KEYS } from "./role-icons";

export function IconSwatchPicker({
  value,
  onChange,
}: {
  value: WorkflowRoleIcon | null;
  onChange: (icon: WorkflowRoleIcon | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        aria-label="No icon"
        aria-pressed={value === null}
        title="No icon"
        onClick={() => onChange(null)}
        className={cn(
          "flex size-7 items-center justify-center rounded-md border border-dashed text-primary/40 ring-offset-background transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          value === null
            ? "border-ring bg-muted text-primary/70 ring-2 ring-ring ring-offset-2"
            : "border-border hover:bg-muted/60 hover:text-primary/70",
        )}
      >
        <X className="size-3.5" />
      </button>
      {ROLE_ICON_KEYS.map((key) => {
        const { Icon, label } = ROLE_ICONS[key];
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            aria-label={label}
            aria-pressed={selected}
            title={label}
            onClick={() => onChange(key)}
            className={cn(
              "flex size-7 items-center justify-center rounded-md border ring-offset-background transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              selected
                ? "border-ring bg-muted text-primary ring-2 ring-ring ring-offset-2"
                : "border-border text-primary/60 hover:bg-muted/60 hover:text-primary",
            )}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}
